import { useEffect, useState } from "react";
import { useAdminSettings, useAdminUpdateSettings } from "../hooks/useApi";
import { normalizeDigits, normalizeDecimal } from "../utils/format";
import "../admin-theme.css";

export default function AdminSettings() {
  const { data: settings, isLoading } = useAdminSettings();
  const updateSettings = useAdminUpdateSettings();

  const [appName, setAppName] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [debug, setDebug] = useState(false);
  const [sandboxMode, setSandboxMode] = useState(true);
  const [walletTaxRate, setWalletTaxRate] = useState("");
  const [walletMinDeposit, setWalletMinDeposit] = useState("");
  const [sessionMaxAge, setSessionMaxAge] = useState("");
  const [usdRateManual, setUsdRateManual] = useState(false);
  const [usdRate, setUsdRate] = useState("");
  const [platformRate, setPlatformRate] = useState("");
  const [usdToIrrFactor, setUsdToIrrFactor] = useState("");
  const [otpRequired, setOtpRequired] = useState(false);
  const [maxOpenTickets, setMaxOpenTickets] = useState("");

  // Load values into local state whenever the server data changes
  useEffect(() => {
    if (!settings) return;
    setAppName(settings.app_name ?? "");
    setAppVersion(settings.app_version ?? "");
    setDebug(Boolean(settings.debug));
    setSandboxMode(Boolean(settings.sandbox_mode));
    setWalletTaxRate(settings.wallet_tax_rate != null ? String(settings.wallet_tax_rate) : "");
    setWalletMinDeposit(settings.wallet_min_deposit != null ? String(settings.wallet_min_deposit) : "");
    setSessionMaxAge(settings.session_max_age != null ? String(settings.session_max_age) : "");
    setUsdRateManual(Boolean(settings.usd_rate_manual));
    setUsdRate(settings.usd_rate != null ? String(settings.usd_rate) : "");
    setPlatformRate(settings.platform_rate != null ? String(settings.platform_rate) : "");
    setUsdToIrrFactor(settings.usd_to_irr_factor != null ? String(settings.usd_to_irr_factor) : "");
    setOtpRequired(Boolean(settings.otp_required));
    setMaxOpenTickets(settings.max_open_tickets != null ? String(settings.max_open_tickets) : "");
  }, [settings]);

  const handleSave = () => {
    const payload: Record<string, unknown> = {
      app_name: appName,
      app_version: appVersion,
      debug,
      sandbox_mode: sandboxMode,
      otp_required: otpRequired,
      usd_rate_manual: usdRateManual,
    };
    const numRate = Number(usdRate.replace(/[^0-9.]/g, ""));
    if (numRate > 0) payload.usd_rate = numRate;
    const taxRate = Number(walletTaxRate.replace(/[^0-9.]/g, ""));
    if (taxRate > 0) payload.wallet_tax_rate = taxRate;
    const minDep = Number(walletMinDeposit.replace(/[^0-9]/g, ""));
    if (minDep > 0) payload.wallet_min_deposit = minDep;
    const maxAge = Number(sessionMaxAge.replace(/[^0-9]/g, ""));
    if (maxAge > 0) payload.session_max_age = maxAge;
    const platRate = Number(platformRate.replace(/[^0-9.]/g, ""));
    if (platRate > 0) payload.platform_rate = platRate;
    const irrFactor = Number(usdToIrrFactor.replace(/[^0-9.]/g, ""));
    if (irrFactor > 0) payload.usd_to_irr_factor = irrFactor;
    const maxTickets = Number(maxOpenTickets.replace(/[^0-9]/g, ""));
    if (maxTickets > 0) payload.max_open_tickets = maxTickets;
    updateSettings.mutate(payload);
  };

  const inputRef = (el: HTMLInputElement | null) => {
    if (el) el.dir = "ltr";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>تنظیمات</h1>
        <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>مدیریت تنظیمات سیستم</p>
      </div>

      {/* Settings Groups */}
      <div className="space-y-5">
        {/* General */}
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header">
            <h2 className="admin-card-title">عمومی</h2>
          </div>
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">نام برنامه</span>
              <input
                ref={inputRef}
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                className="admin-input ltr"
                style={{ width: 200, height: 38, textAlign: "center" }}
              />
            </div>
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">نسخه</span>
              <input
                ref={inputRef}
                type="text"
                value={appVersion}
                onChange={(e) => setAppVersion(e.target.value)}
                className="admin-input ltr"
                style={{ width: 120, height: 38, textAlign: "center" }}
              />
            </div>
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">دامنه</span>
              <span className="text-sm text-left ltr" style={{ color: "var(--admin-text-primary)" }}>{settings?.domain}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">حالت debug</span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: debug ? "var(--admin-success)" : "var(--admin-text-tertiary)" }}>
                  {debug ? "فعال" : "غیرفعال"}
                </span>
                <button
                  type="button"
                  className={`admin-toggle${debug ? " on" : ""}`}
                  onClick={() => setDebug((v) => !v)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">حالت نگهداری</span>
              <span className="text-sm font-medium" style={{ color: settings?.maintenance_mode ? "var(--admin-warning)" : "var(--admin-text-tertiary)" }}>
                {settings?.maintenance_mode ? "فعال" : "غیرفعال"}
              </span>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header">
            <h2 className="admin-card-title">پرداخت</h2>
          </div>
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">درگاه پرداخت</span>
              <span className="text-sm text-left ltr" style={{ color: "var(--admin-text-primary)" }}>ZarinPal</span>
            </div>

            {/* Sandbox toggle */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">حالت sandbox</span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: sandboxMode ? "var(--admin-success)" : "var(--admin-text-tertiary)" }}>
                  {sandboxMode ? "فعال" : "غیرفعال"}
                </span>
                <button
                  type="button"
                  className={`admin-toggle${sandboxMode ? " on" : ""}`}
                  onClick={() => setSandboxMode((v) => !v)}
                />
              </div>
            </div>

            {/* Minimum deposit */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">حداقل شارژ کیف پول (ریال)</span>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                value={walletMinDeposit}
                onChange={(e) => setWalletMinDeposit(normalizeDigits(e.target.value))}
                className="admin-input ltr"
                style={{ width: 160, height: 38, textAlign: "center" }}
              />
            </div>

            {/* Tax rate */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">نرخ مالیات</span>
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={walletTaxRate}
                onChange={(e) => setWalletTaxRate(normalizeDecimal(e.target.value))}
                className="admin-input ltr"
                style={{ width: 120, height: 38, textAlign: "center" }}
                placeholder="0.1"
              />
            </div>
          </div>
        </div>

        {/* Pricing / Platform */}
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header">
            <h2 className="admin-card-title">قیمت‌گذاری</h2>
          </div>
          <div className="p-2">
            {/* Platform rate */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">ضریب پلتفرم</span>
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={platformRate}
                onChange={(e) => setPlatformRate(normalizeDecimal(e.target.value))}
                className="admin-input ltr"
                style={{ width: 120, height: 38, textAlign: "center" }}
                placeholder="5.5"
              />
            </div>

            {/* USD rate mode */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">نرخ تبدیل دلار</span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: usdRateManual ? "var(--admin-warning)" : "var(--admin-text-tertiary)" }}>
                  {usdRateManual ? "دستی" : "خودکار"}
                </span>
                <button
                  type="button"
                  className={`admin-toggle${usdRateManual ? " on" : ""}`}
                  onClick={() => setUsdRateManual((v) => !v)}
                />
              </div>
            </div>

            {/* USD rate input */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">نرخ تبدیل دلار به تومان</span>
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={usdRate}
                  onChange={(e) => setUsdRate(normalizeDigits(e.target.value))}
                  disabled={!usdRateManual}
                  className="admin-input"
                  style={{
                    width: 160,
                    height: 38,
                    textAlign: "center",
                    fontFamily: "'Vazirmatn', 'Inter', system-ui, sans-serif",
                    fontWeight: 600,
                    opacity: usdRateManual ? 1 : 0.5,
                  }}
                  placeholder="مثلاً ۱۹۵۰۰۰۰"
                />
                <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>تومان</span>
              </div>
            </div>

            {/* USD to IRR factor input */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">ضریب تبدیل دلار به ریال</span>
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={usdToIrrFactor}
                onChange={(e) => setUsdToIrrFactor(normalizeDecimal(e.target.value))}
                className="admin-input ltr"
                style={{ width: 140, height: 38, textAlign: "center" }}
                placeholder="0.5"
              />
            </div>

            {usdRateManual && (
              <p className="px-3 pb-2 text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                در حالت دستی، نرخ به‌روزرسانی خودکار اعمال نمی‌شود و فقط از همین‌جا قابل تغییر است.
              </p>
            )}
          </div>
        </div>

        {/* Session & Security */}
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header">
            <h2 className="admin-card-title">نشست و امنیت</h2>
          </div>
          <div className="p-2">
            {/* Session max age */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">مدت اعتبار نشست (ثانیه)</span>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                value={sessionMaxAge}
                onChange={(e) => setSessionMaxAge(normalizeDigits(e.target.value))}
                className="admin-input ltr"
                style={{ width: 140, height: 38, textAlign: "center" }}
                placeholder="86400"
              />
            </div>

            {/* OTP required */}
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">نیاز به کد تأیید (OTP)</span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: otpRequired ? "var(--admin-success)" : "var(--admin-text-tertiary)" }}>
                  {otpRequired ? "فعال" : "غیرفعال"}
                </span>
                <button
                  type="button"
                  className={`admin-toggle${otpRequired ? " on" : ""}`}
                  onClick={() => setOtpRequired((v) => !v)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header">
            <h2 className="admin-card-title">پشتیبانی</h2>
          </div>
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">حداکثر تیکت‌های باز هر کاربر</span>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                value={maxOpenTickets}
                onChange={(e) => setMaxOpenTickets(normalizeDigits(e.target.value))}
                className="admin-input ltr"
                style={{ width: 100, height: 38, textAlign: "center" }}
                placeholder="10"
              />
            </div>
            <p className="px-3 pb-2 text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
              وقتی کاربر به این سقف برسد، امکان ثبت تیکت جدید نخواهد داشت تا یکی از تیکت‌های باز را ببندد.
            </p>
          </div>
        </div>

        {/* Registration */}
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header">
            <h2 className="admin-card-title">ثبت‌نام</h2>
          </div>
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">ثبت‌نام کاربران</span>
              <span className="text-sm font-medium" style={{ color: settings?.registration_enabled ? "var(--admin-success)" : "var(--admin-text-tertiary)" }}>
                {settings?.registration_enabled ? "فعال" : "غیرفعال"}
              </span>
            </div>

            <div className="flex items-center justify-between px-3 py-3.5 rounded-xl" style={{ color: "var(--admin-text-secondary)" }}>
              <span className="text-sm">ایمیل مدیر</span>
              <span className="text-sm text-left ltr" style={{ color: "var(--admin-text-primary)" }}>{settings?.admin_email}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className="admin-btn admin-btn-primary"
          disabled={updateSettings.isPending}
          onClick={handleSave}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          {updateSettings.isPending ? "در حال ذخیره..." : "ذخیره تغییرات"}
        </button>
      </div>
    </div>
  );
}